/**
 * Dialog para reconexão de inboxes Instagram (Meta)
 * 
 * Exibe credenciais configuradas, permite testar conexão via Graph API
 * e mostra feedback detalhado de erros retornados pela Meta.
 * 
 * @release v1.0.1 - 2026-02-01
 * Correção: Leitura correta de data.data do retorno da Edge Function provider-proxy.
 * Arquitetura multi-provider via provider-proxy Edge Function.
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  ExternalLink,
  Instagram,
  Check,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { logger } from '@/lib/logger';

/**
 * Props do componente InstagramReconnectDialog
 */
interface InstagramReconnectDialogProps {
  /** Controla se o dialog está aberto */
  open: boolean;
  /** Callback para alterar estado de abertura */
  onOpenChange: (open: boolean) => void;
  /** ID da inbox a ser reconectada */
  inboxId: string;
  /** Nome da inbox para exibição */
  inboxName: string;
  /** Callback executado após reconexão bem-sucedida */
  onConnected: () => void;
  /** Status inicial da inbox (da tabela inboxes.status) - usado para badge dinâmico */
  initialStatus?: 'active' | 'inactive' | 'pending_setup' | 'disconnected' | 'error' | 'connecting';
  /** Callback para abrir modal de edição de credenciais (opcional - para modais empilhados) */
  onEditCredentials?: () => void;
}

/**
 * Tipo do resultado do teste de conexão
 */
interface ConnectionTestResult {
  success: boolean;
  status?: string;
  username?: string;
  error_code?: string | number;
  error_message?: string;
  error_type?: string;
}

/**
 * Tipo das credenciais da inbox Instagram
 */
interface InstagramCredentials {
  instagram_business_account_id?: string;
  page_id?: string;
  access_token?: string;
  app_secret?: string;
}

/**
 * Cor oficial do Instagram (gradiente simplificado)
 */
const INSTAGRAM_COLOR = '#E4405F';

/**
 * Hook para buscar credenciais da inbox
 */
function useInboxCredentials(inboxId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['inbox-credentials', inboxId],
    queryFn: async () => {
      logger.info('[InstagramReconnect] Buscando credenciais da inbox:', inboxId);
      
      const { data, error } = await supabase
        .from('inbox_credentials')
        .select('credentials')
        .eq('inbox_id', inboxId)
        .maybeSingle();

      if (error) {
        logger.error('[InstagramReconnect] Erro ao buscar credenciais:', error.message);
        throw error;
      }

      return (data?.credentials as InstagramCredentials) || null;
    },
    enabled,
    staleTime: 30000, // 30 segundos
  });
}

/**
 * Dialog para reconexão de inbox Instagram
 * Exibe credenciais, permite testar conexão e mostra erros detalhados da Meta
 */
export function InstagramReconnectDialog({
  open,
  onOpenChange,
  inboxId,
  inboxName,
  onConnected,
  initialStatus,
  onEditCredentials,
}: InstagramReconnectDialogProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // Estado do teste de conexão
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(null);
  
  // Busca credenciais da inbox
  const { data: credentials, isLoading: isLoadingCredentials } = useInboxCredentials(inboxId, open);

  /**
   * Executa teste de conexão via provider-proxy
   */
  const handleTestConnection = useCallback(async () => {
    setIsTesting(true);
    setTestResult(null);
    
    try {
      logger.info('[InstagramReconnect] Testando conexão para inbox:', inboxId);
      
      const { data, error } = await supabase.functions.invoke('provider-proxy', {
        body: {
          action: 'get_connection_status',
          inbox_id: inboxId,
        },
      });

      if (error) {
        logger.error('[InstagramReconnect] Erro na Edge Function:', error);
        setTestResult({
          success: false,
          error_message: error.message || 'Erro ao conectar com o servidor',
        });
        return;
      }

      logger.info('[InstagramReconnect] Resultado do teste:', data);

      // CORREÇÃO v1.0.1: provider-proxy retorna { success, data: { status, ... } }
      // Precisa ler o payload aninhado corretamente
      const payload = data?.data || data;  // Fallback para compatibilidade
      
      logger.info('[InstagramReconnect] Payload extraído:', payload);

      // Verifica se conexão está ativa
      if (payload?.status === 'active' && payload?.connected === true) {
        setTestResult({
          success: true,
          status: 'active',
          username: payload.username,
        });
        
        // Invalida cache de inboxes desconectadas
        queryClient.invalidateQueries({ queryKey: ['disconnected-inboxes'] });
        
        // Chama callback de sucesso após pequeno delay para UX
        setTimeout(() => {
          onConnected();
        }, 1500);
      } else {
        // Conexão falhou - extrai detalhes do erro do payload correto
        setTestResult({
          success: false,
          status: payload?.status,
          error_code: payload?.error_code,
          error_message: payload?.error_message || payload?.message || data?.error || 'Falha na conexão',
          error_type: payload?.error_type,
        });
      }
    } catch (err) {
      logger.error('[InstagramReconnect] Exceção no teste:', err);
      setTestResult({
        success: false,
        error_message: err instanceof Error ? err.message : 'Erro desconhecido',
      });
    } finally {
      setIsTesting(false);
    }
  }, [inboxId, queryClient, onConnected]);

  /**
   * Abre modal de edição de credenciais
   * Se callback foi fornecido pelo pai, usa ele (para modais empilhados)
   * Caso contrário, navega para página de detalhes (fallback)
   */
  const handleEditCredentials = useCallback(() => {
    // Se callback de edição foi fornecido, usa ele (para abrir modal sobreposto)
    if (onEditCredentials) {
      onEditCredentials();
      return;
    }
    
    // Fallback: navega para página de detalhes (comportamento antigo)
    onOpenChange(false);
    navigate(`/tenant/inboxes/${inboxId}`);
  }, [inboxId, navigate, onOpenChange, onEditCredentials]);

  /**
   * Verifica se uma credencial está configurada (não vazia)
   */
  const isCredentialSet = (value?: string): boolean => {
    return Boolean(value && value.trim().length > 0);
  };

  /**
   * Mascara parcialmente um ID para exibição segura
   * Exibe primeiros 8 e últimos 4 caracteres
   */
  const maskId = (id?: string): string => {
    if (!id || id.length <= 12) return id || '—';
    return `${id.substring(0, 8)}...${id.substring(id.length - 4)}`;
  };

  /**
   * Renderiza ícone de status de uma credencial
   */
  const CredentialStatus = ({ isSet }: { isSet: boolean }) => (
    isSet ? (
      <Check className="h-4 w-4 text-emerald-500" />
    ) : (
      <X className="h-4 w-4 text-destructive" />
    )
  );

  /**
   * Renderiza badge de status dinâmico baseado em testResult ou initialStatus
   */
  const getStatusBadge = () => {
    // Se testou e funcionou → verde
    if (testResult?.success) {
      return (
        <Badge variant="outline" className="border-emerald-500/50 text-emerald-600 dark:text-emerald-400">
          Conectado
        </Badge>
      );
    }
    
    // Se testou e falhou → vermelho
    if (testResult && !testResult.success) {
      return (
        <Badge variant="outline" className="border-destructive/50 text-destructive">
          Desconectado
        </Badge>
      );
    }
    
    // Antes de testar → usar status inicial
    if (initialStatus === 'active') {
      return (
        <Badge variant="outline" className="border-emerald-500/50 text-emerald-600 dark:text-emerald-400">
          Ativo
        </Badge>
      );
    }
    
    if (initialStatus === 'error') {
      return (
        <Badge variant="outline" className="border-destructive/50 text-destructive">
          Erro
        </Badge>
      );
    }
    
    if (initialStatus === 'disconnected') {
      return (
        <Badge variant="outline" className="border-amber-500/50 text-amber-600 dark:text-amber-400">
          Desconectado
        </Badge>
      );
    }
    
    if (initialStatus === 'pending_setup') {
      return (
        <Badge variant="outline" className="border-amber-500/50 text-amber-600 dark:text-amber-400">
          Pendente
        </Badge>
      );
    }
    
    if (initialStatus === 'connecting') {
      return (
        <Badge variant="outline" className="border-blue-500/50 text-blue-600 dark:text-blue-400">
          Conectando
        </Badge>
      );
    }
    
    // Default: pendente verificação
    return (
      <Badge variant="outline" className="border-amber-500/50 text-amber-600 dark:text-amber-400">
        Verificar
      </Badge>
    );
  };

  /**
   * Renderiza área de resultado do teste
   */
  const renderTestResult = () => {
    if (!testResult) return null;

    if (testResult.success) {
      return (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
          <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
              Conexão ativa
            </p>
            {testResult.username && (
              <p className="text-xs text-muted-foreground mt-0.5">
                Conta: @{testResult.username}
              </p>
            )}
          </div>
        </div>
      );
    }

    // Determina cor e ícone baseado no tipo de erro
    const isTokenError = testResult.error_code === 190 || 
                         testResult.error_message?.toLowerCase().includes('token');
    const isConfigError = testResult.error_code === 100 ||
                          testResult.error_message?.toLowerCase().includes('invalid');

    return (
      <div 
        className={cn(
          "flex items-start gap-3 p-4 rounded-lg border",
          isTokenError 
            ? "bg-destructive/10 border-destructive/20" 
            : isConfigError
              ? "bg-amber-500/10 border-amber-500/20"
              : "bg-destructive/10 border-destructive/20"
        )}
      >
        {isTokenError ? (
          <XCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
        ) : isConfigError ? (
          <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
        ) : (
          <XCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className={cn(
            "text-sm font-medium",
            isTokenError 
              ? "text-destructive" 
              : isConfigError 
                ? "text-amber-600 dark:text-amber-400"
                : "text-destructive"
          )}>
            {isTokenError ? 'Token inválido ou expirado' : 
             isConfigError ? 'Configuração inválida' : 
             'Erro na conexão'}
          </p>
          
          {/* Código do erro */}
          {testResult.error_code && (
            <p className="text-xs text-muted-foreground mt-1">
              Código: {testResult.error_code}
              {testResult.error_type && ` (${testResult.error_type})`}
            </p>
          )}
          
          {/* Mensagem detalhada da Meta */}
          {testResult.error_message && (
            <div className="mt-2 p-2 rounded bg-muted/50">
              <p className="text-xs text-muted-foreground font-mono break-words">
                {testResult.error_message}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] p-0 overflow-hidden bg-card border-border">
        {/* Header com branding Instagram */}
        <div className="relative px-6 pt-6 pb-4">
          {/* Background gradiente sutil */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{ 
              background: `linear-gradient(135deg, ${INSTAGRAM_COLOR} 0%, transparent 60%)`
            }}
          />
          
          <DialogHeader className="relative">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                {/* Ícone Instagram estilizado */}
                <div 
                  className="h-12 w-12 rounded-xl flex items-center justify-center shadow-lg"
                  style={{ 
                    background: 'linear-gradient(135deg, #833AB4 0%, #FD1D1D 50%, #FCB045 100%)'
                  }}
                >
                  <Instagram className="h-6 w-6 text-white" />
                </div>
                <div>
                  <DialogTitle className="text-lg font-semibold text-foreground">
                    Reconectar Instagram
                  </DialogTitle>
                  <p className="text-sm text-muted-foreground line-clamp-1">
                    {inboxName}
                  </p>
                </div>
              </div>
              
              {/* Badge de status */}
              {/* Badge de status dinâmico */}
              {getStatusBadge()}
            </div>
          </DialogHeader>
        </div>

        {/* Conteúdo principal */}
        <div className="px-6 pb-6 space-y-4">
          {/* Seção de Credenciais */}
          <div>
            <h3 className="text-sm font-medium text-foreground mb-3">
              Credenciais Configuradas
            </h3>
            
            {isLoadingCredentials ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-2 p-3 rounded-lg bg-muted/30 border">
                {/* Instagram Business Account ID */}
                <div className="flex items-center justify-between py-1.5">
                  <span className="text-xs text-muted-foreground">
                    Instagram Business Account ID
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-foreground">
                      {maskId(credentials?.instagram_business_account_id)}
                    </span>
                    <CredentialStatus isSet={isCredentialSet(credentials?.instagram_business_account_id)} />
                  </div>
                </div>
                
                {/* Access Token */}
                <div className="flex items-center justify-between py-1.5 border-t border-border/50">
                  <span className="text-xs text-muted-foreground">
                    Access Token
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground">
                      {isCredentialSet(credentials?.access_token) ? 'Configurado' : '—'}
                    </span>
                    <CredentialStatus isSet={isCredentialSet(credentials?.access_token)} />
                  </div>
                </div>
                
                {/* App Secret */}
                <div className="flex items-center justify-between py-1.5 border-t border-border/50">
                  <span className="text-xs text-muted-foreground">
                    App Secret
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground">
                      {isCredentialSet(credentials?.app_secret) ? 'Configurado' : '—'}
                    </span>
                    <CredentialStatus isSet={isCredentialSet(credentials?.app_secret)} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Resultado do Teste */}
          {renderTestResult()}

          {/* Botões de Ação */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleEditCredentials}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Editar Credenciais
            </Button>
            
            <Button
              className="flex-1"
              onClick={handleTestConnection}
              disabled={isTesting}
              style={{ 
                background: isTesting ? undefined : INSTAGRAM_COLOR,
                borderColor: INSTAGRAM_COLOR 
              }}
            >
              {isTesting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Testando...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Testar Conexão
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
